import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import { type IMaskUnlockRequest, type IMaskUnlockResponse } from "../types";
import { extractApiErrorMessage } from "../utils/apiError";

// APIからエラーメッセージを取得できなかった場合に表示するフォールバックメッセージ
const DEFAULT_MASK_UNLOCK_ERROR_MESSAGE = "マスク解除に失敗しました。";

// サーバーからレスポンスを受け取れなかった場合（通信断・タイムアウト等）に表示するメッセージ。
// バックエンドは冪等性を保証しているため、この状態から再実行しても二重課金は発生しない。
const NO_RESPONSE_MASK_UNLOCK_ERROR_MESSAGE =
  "通信状況により処理結果を確認できませんでした。課金されている可能性があるため、購入済みフィルタや利用明細をご確認ください。再度実行しても二重に課金されることはありません。";

interface IUseMaskUnlockReturnType {
  unlock: (ids: number[]) => Promise<IMaskUnlockResponse | null>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export const useMaskUnlock = (): IUseMaskUnlockReturnType => {
  const { apiClient } = useApiClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unlock = useCallback(
    async (ids: number[]): Promise<IMaskUnlockResponse | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await apiClient.post<IMaskUnlockResponse>(
          "commercial-reception-book/mask-unlock",
          {
            commercialReceptionBookIds: ids,
          } satisfies IMaskUnlockRequest
        );
        return data;
      } catch (e) {
        Sentry.captureException(e);
        if (isAxiosError(e) && !e.response) {
          // リクエストは送信されたがレスポンスを受け取れなかった（通信断・タイムアウト等）。
          // サーバー側で処理が完了している可能性があるため、専用のメッセージで注意喚起する。
          setError(NO_RESPONSE_MASK_UNLOCK_ERROR_MESSAGE);
          return null;
        }
        // バックエンドが返す実際のエラーメッセージを優先して表示し、
        // 取得できなかった場合のみ汎用メッセージにフォールバックする
        setError(
          extractApiErrorMessage(e) ?? DEFAULT_MASK_UNLOCK_ERROR_MESSAGE
        );
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient]
  );

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return { unlock, isLoading, error, clearError };
};
